import React, { useState } from "react";
import { Button, Icon, IconButton, Input } from "../..";

const WidgetConfigurator = ({ config, widgetType, onChange }) => {
  const copyUrlLabel = "Copy URL";
  const [copiedUrl, setCopiedUrl] = useState(false);

  const handleCopyUrl = async () => {
    if (!config.chatUrl) return;
    try {
      await navigator.clipboard.writeText(config.chatUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };
  const handleNumericChange = (key, value) => {
    // Only allow digits, max 4 digits (9999)
    if (value !== "" && !/^\d*$/.test(value)) return;
    if (value.length > 4) return;
    onChange(key, value);
  };

  const handleNumericBlur = (key, defaultValue, min) => {
    const numValue = parseInt(config[key], 10);
    if (isNaN(numValue) || config[key] === "") {
      onChange(key, defaultValue);
    } else if (numValue < min) {
      onChange(key, min);
    }
  };

  const positions = [
    { value: "bottom-left", label: "Bottom Left", iconName: "ArrowDownLeft" },
    {
      value: "bottom-right",
      label: "Bottom Right",
      iconName: "ArrowDownRight",
    },
  ];

  return (
    <div className="ordering-widget-configurator space-y-8">
      {/* Chat URL */}
      <div className="space-y-3">
        <label
          htmlFor="widget-chat-url"
          className="text-card-foreground flex items-center gap-2 text-sm font-medium"
        >
          <Icon name="Link" className="text-primary h-4 w-4" />
          Chat URL
        </label>
        <div className="flex gap-2">
          <Input
            id="widget-chat-url"
            type="url"
            value={config.chatUrl}
            disabled={true}
            onChange={(e) => onChange("chatUrl", e.target.value)}
            placeholder="https://your-chat.finitless.ai/"
            containerClassName="flex-1"
          />
          <IconButton
            type="button"
            emphasis="outline"
            intent="neutral"
            onClick={handleCopyUrl}
            disabled={!config.chatUrl}
            aria-label={copyUrlLabel}
            title={copyUrlLabel}
          >
            <Icon
              name={copiedUrl ? "Check" : "Copy"}
              className={`h-5 w-5 ${copiedUrl ? "text-success" : ""}`}
            />
          </IconButton>
        </div>
        <p className="text-muted-foreground text-xs">
          The URL of your Finitless AI chat application
        </p>
      </div>

      {/* Icon URL (only for floating) */}
      {widgetType === "floating" && (
        <div className="space-y-3">
          <label
            htmlFor="widget-icon-url"
            className="text-card-foreground flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="Image" className="text-primary h-4 w-4" />
            Button Icon URL (optional)
          </label>
          <div className="flex gap-3">
            {config.iconUrl && (
              <div className="border-border bg-background flex h-12 w-14 items-center justify-center overflow-hidden rounded-lg border">
                <img
                  src={config.iconUrl}
                  alt="Icon preview"
                  className="h-8 w-8 object-contain"
                  onError={(e) => (e.target.style.display = "none")}
                />
              </div>
            )}
            <Input
              id="widget-icon-url"
              type="url"
              value={config.iconUrl || ""}
              onChange={(e) => onChange("iconUrl", e.target.value)}
              placeholder="https://example.com/icon.png"
              containerClassName="flex-1"
            />
          </div>
          <p className="text-muted-foreground text-xs">
            Custom icon for the chat button. Leave empty to use the default chat
            icon.
          </p>
        </div>
      )}

      {/* Colors */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-3">
          <label
            id="widget-primary-color-label"
            htmlFor="widget-primary-color"
            className="text-card-foreground flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="Palette" className="text-primary h-4 w-4" />
            Primary Color
          </label>
          <div className="flex gap-3">
            <div
              className="ordering-widget-customer-color-preview border-border relative h-12 w-14 cursor-pointer overflow-hidden rounded-lg border"
              style={{ backgroundColor: config.primaryColor }}
            >
              <input
                type="color"
                value={config.primaryColor}
                onChange={(e) => onChange("primaryColor", e.target.value)}
                aria-labelledby="widget-primary-color-label"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </div>
            <Input
              id="widget-primary-color"
              type="text"
              value={config.primaryColor}
              onChange={(e) => onChange("primaryColor", e.target.value)}
              containerClassName="flex-1"
              className="font-mono"
            />
          </div>
        </div>

        <div className="space-y-3">
          <label
            id="widget-secondary-color-label"
            htmlFor="widget-secondary-color"
            className="text-card-foreground flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="Palette" className="text-primary h-4 w-4" />
            Secondary Color
          </label>
          <div className="flex gap-3">
            <div
              className="ordering-widget-customer-color-preview border-border relative h-12 w-14 cursor-pointer overflow-hidden rounded-lg border"
              style={{ backgroundColor: config.secondaryColor }}
            >
              <input
                type="color"
                value={config.secondaryColor}
                onChange={(e) => onChange("secondaryColor", e.target.value)}
                aria-labelledby="widget-secondary-color-label"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </div>
            <Input
              id="widget-secondary-color"
              type="text"
              value={config.secondaryColor}
              onChange={(e) => onChange("secondaryColor", e.target.value)}
              containerClassName="flex-1"
              className="font-mono"
            />
          </div>
        </div>
      </div>

      {/* Gradient Preview */}
      <div className="border-border rounded-xl border p-4">
        <p className="text-muted-foreground mb-3 text-sm">Gradient preview:</p>
        <div
          className="ordering-widget-customer-color-preview h-16 rounded-lg"
          style={{
            background: `linear-gradient(135deg, ${config.primaryColor} 0%, ${config.secondaryColor} 100%)`,
          }}
        />
      </div>

      {/* Position (for floating only) */}
      {widgetType === "floating" && (
        <div className="space-y-3">
          <p
            id="widget-position-label"
            className="text-card-foreground flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="Move" className="text-primary h-4 w-4" />
            Button Position
          </p>
          <div
            role="group"
            aria-labelledby="widget-position-label"
            className="grid grid-cols-2 gap-3"
          >
            {positions.map((pos) => (
              <Button
                key={pos.value}
                type="button"
                variant="outline"
                size="touch"
                onClick={() => onChange("position", pos.value)}
                aria-pressed={config.position === pos.value}
                className={`h-auto p-4 ${
                  config.position === pos.value
                    ? "border-primary bg-accent"
                    : "border-border hover:border-primary"
                } `}
              >
                <Icon name={pos.iconName} className="text-primary h-6 w-6" />
                <span className="text-card-foreground text-sm">
                  {pos.label}
                </span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Sizes (for floating only) */}
      {widgetType === "floating" && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="space-y-3">
            <label
              htmlFor="widget-floating-button-size"
              className="text-card-foreground flex items-center gap-2 text-sm font-medium"
            >
              <Icon name="Circle" className="text-primary h-4 w-4" />
              Button Size (min. 40px)
            </label>
            <Input
              id="widget-floating-button-size"
              type="text"
              inputMode="numeric"
              value={config.buttonSize}
              onChange={(e) =>
                handleNumericChange("buttonSize", e.target.value)
              }
              onBlur={() => handleNumericBlur("buttonSize", 60, 40)}
            />
          </div>

          <div className="space-y-3">
            <label
              htmlFor="widget-floating-chat-width"
              className="text-card-foreground flex items-center gap-2 text-sm font-medium"
            >
              <Icon name="ArrowLeftRight" className="text-primary h-4 w-4" />
              Chat Width (min. 300px)
            </label>
            <Input
              id="widget-floating-chat-width"
              type="text"
              inputMode="numeric"
              value={config.chatWidth}
              onChange={(e) => handleNumericChange("chatWidth", e.target.value)}
              onBlur={() => handleNumericBlur("chatWidth", 370, 300)}
            />
          </div>

          <div className="space-y-3">
            <label
              htmlFor="widget-floating-chat-height"
              className="text-card-foreground flex items-center gap-2 text-sm font-medium"
            >
              <Icon name="ArrowUpDown" className="text-primary h-4 w-4" />
              Chat Height (min. 400px)
            </label>
            <Input
              id="widget-floating-chat-height"
              type="text"
              inputMode="numeric"
              value={config.chatHeight}
              onChange={(e) =>
                handleNumericChange("chatHeight", e.target.value)
              }
              onBlur={() => handleNumericBlur("chatHeight", 570, 400)}
            />
          </div>
        </div>
      )}

      {/* Inline sizes */}
      {widgetType === "inline" && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <label
              htmlFor="widget-inline-chat-width"
              className="text-card-foreground flex items-center gap-2 text-sm font-medium"
            >
              <Icon name="ArrowLeftRight" className="text-primary h-4 w-4" />
              Max Width (min. 300px)
            </label>
            <Input
              id="widget-inline-chat-width"
              type="text"
              inputMode="numeric"
              value={config.chatWidth}
              onChange={(e) => handleNumericChange("chatWidth", e.target.value)}
              onBlur={() => handleNumericBlur("chatWidth", 450, 300)}
            />
          </div>

          <div className="space-y-3">
            <label
              htmlFor="widget-inline-chat-height"
              className="text-card-foreground flex items-center gap-2 text-sm font-medium"
            >
              <Icon name="ArrowUpDown" className="text-primary h-4 w-4" />
              Height (min. 400px)
            </label>
            <Input
              id="widget-inline-chat-height"
              type="text"
              inputMode="numeric"
              value={config.chatHeight}
              onChange={(e) =>
                handleNumericChange("chatHeight", e.target.value)
              }
              onBlur={() => handleNumericBlur("chatHeight", 600, 400)}
            />
          </div>
        </div>
      )}

      {/* Popup sizes */}
      {widgetType === "popup" && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <label
              htmlFor="widget-popup-chat-width"
              className="text-card-foreground flex items-center gap-2 text-sm font-medium"
            >
              <Icon name="ArrowLeftRight" className="text-primary h-4 w-4" />
              Max Width (min. 300px)
            </label>
            <Input
              id="widget-popup-chat-width"
              type="text"
              inputMode="numeric"
              value={config.chatWidth}
              onChange={(e) => handleNumericChange("chatWidth", e.target.value)}
              onBlur={() => handleNumericBlur("chatWidth", 450, 300)}
            />
          </div>

          <div className="space-y-3">
            <label
              htmlFor="widget-popup-chat-height"
              className="text-card-foreground flex items-center gap-2 text-sm font-medium"
            >
              <Icon name="ArrowUpDown" className="text-primary h-4 w-4" />
              Max Height (min. 400px)
            </label>
            <Input
              id="widget-popup-chat-height"
              type="text"
              inputMode="numeric"
              value={config.chatHeight}
              onChange={(e) =>
                handleNumericChange("chatHeight", e.target.value)
              }
              onBlur={() => handleNumericBlur("chatHeight", 650, 400)}
            />
          </div>
        </div>
      )}

      {/* Show Header toggle (for popup only) */}
      {widgetType === "popup" && (
        <div className="space-y-3">
          <p
            id="widget-modal-header-label"
            className="text-card-foreground flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="PanelTop" className="text-primary h-4 w-4" />
            Modal Header
          </p>
          <div
            role="group"
            aria-labelledby="widget-modal-header-label"
            className="flex gap-3"
          >
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() => onChange("showHeader", true)}
              aria-pressed={config.showHeader}
              className={`h-auto flex-1 p-4 ${
                config.showHeader
                  ? "border-primary bg-accent"
                  : "border-border hover:border-primary"
              } `}
            >
              <Icon name="Check" className="text-primary h-5 w-5" />
              <span className="text-card-foreground text-sm">With Header</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() => onChange("showHeader", false)}
              aria-pressed={!config.showHeader}
              className={`h-auto flex-1 p-4 ${
                !config.showHeader
                  ? "border-primary bg-accent"
                  : "border-border hover:border-primary"
              } `}
            >
              <Icon name="X" className="text-primary h-5 w-5" />
              <span className="text-card-foreground text-sm">
                Without Header
              </span>
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            {config.showHeader
              ? "The modal will have a gradient header with a close button"
              : "The modal will display the chat directly without a header bar"}
          </p>
        </div>
      )}
    </div>
  );
};

export default WidgetConfigurator;
